import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Platform, StyleSheet, View } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Pressable } from '@/components/button/Pressable';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';

import DateTimePicker, { DateTimePickerEvent } from './DateTimePicker';

type DobPickerProps = {
    date: Date;
    showDobPicker?: boolean;
    setShowDobPicker?: (show: boolean) => void;
    handleDatePickerChange?: (value: Date) => void;
};

export const DobPicker = ({ date, showDobPicker, setShowDobPicker, handleDatePickerChange }: DobPickerProps) => {
    const { t } = useTranslation(['common']);
    const slideAnim = useRef(new Animated.Value(300)).current;

    useEffect(() => {
        if (showDobPicker) {
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 300,
                useNativeDriver: true,
            }).start();
        } else {
            Animated.timing(slideAnim, {
                toValue: 300,
                duration: 300,
                useNativeDriver: true,
            }).start();
        }
    }, [showDobPicker, slideAnim]);

    const handleOverlayPress = () => {
        setShowDobPicker?.(false);
    };

    const onChange = (event: DateTimePickerEvent, selectedDate?: Date) => {
        // On Android the picker is a modal dialog that dismisses itself; the component
        // must be unmounted before the next re-render or the dialog re-opens.
        if (Platform.OS === 'android') setShowDobPicker?.(false);
        if (event.type !== 'dismissed' && selectedDate) handleDatePickerChange?.(selectedDate);
    };

    if (!showDobPicker) return null;

    return (
        <>
            <Pressable onPress={handleOverlayPress}>
                <View style={styles.overlay} />
            </Pressable>
            <Animated.View
                style={[
                    styles.datePickerContainer,
                    {
                        transform: [{ translateY: slideAnim }],
                    },
                ]}
            >
                <Box flexDirection="row" justifyContent="flex-end" paddingVertical="s12" paddingHorizontal="s12">
                    <Text textAlign="right" testID="DobDoneBtn" onPress={() => setShowDobPicker?.(false)}>
                        {t('common:done')}
                    </Text>
                </Box>
                <Box flexDirection="row" justifyContent="center">
                    <DateTimePicker
                        testID="DobDateTimePicker"
                        value={date}
                        display="spinner"
                        mode="date"
                        themeVariant="dark"
                        onChange={onChange}
                    />
                </Box>
            </Animated.View>
        </>
    );
};

const styles = StyleSheet.create({
    datePickerContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: ds.colorValue.background.neutral.subtle,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        elevation: 5,
        zIndex: 1,
    },
    overlay: {
        ...StyleSheet.absoluteFillObject,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
});
